import { useCallback, useEffect, useRef } from 'react';

/** Tab-focusable descendants of `root`, in document order (skips disabled/hidden controls). */
function focusableWithin(root: HTMLElement): HTMLElement[] {
  const selector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');
  return Array.from(root.querySelectorAll<HTMLElement>(selector)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement,
  );
}

/**
 * Accessible-modal focus management shared by {@link Dialog} and {@link ActionSheet}. On open it moves
 * focus into the panel (the first focusable control, else the panel itself) and TRAPS Tab/Shift+Tab
 * within it; on close it restores focus to the element that was focused before opening. Returns the
 * panel ref to attach + the keydown handler to wire (Escape closes; Tab is trapped). Keeping this in one
 * place means a fix to the trap (shadow DOM, `inert`, a focus bug) lands for both surfaces at once.
 */
export function useFocusTrap(
  open: boolean,
  onClose: () => void,
): {
  panelRef: React.RefObject<HTMLDivElement | null>;
  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
} {
  const panelRef = useRef<HTMLDivElement>(null);
  // The element focused just before the surface opened, so we can restore focus on close.
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>): void => {
      if (e.key === 'Escape') {
        // Stop here so a nested trap (e.g. a ConfirmDialog inside an open ActionSheet) doesn't also
        // bubble the same keypress up to an ancestor trap's onKeyDown — one Escape closes only the
        // topmost surface.
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = focusableWithin(panel);
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) {
        // Nothing focusable inside — keep focus on the panel rather than escaping the surface.
        e.preventDefault();
        panel.focus();
        return;
      }
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || active === panel) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const panel = panelRef.current;
    // Move focus into the surface: the first focusable control, else the panel itself.
    const focusables = panel ? focusableWithin(panel) : [];
    (focusables[0] ?? panel)?.focus();
    return () => {
      // Restore focus to the trigger on close (only if it's still in the document).
      const prev = previouslyFocused.current;
      if (prev && document.contains(prev)) prev.focus();
    };
  }, [open]);

  return { panelRef, onKeyDown };
}
